/**
 * CSV解析結果を型安全に正規化するモデル群。
 * 既存の `processCsvResults` が生成するデータを構造化し、
 * RSST/VS 各プロトコルコンポーネントで一貫して使用する。
 */

export type MeasurementEventType = 'swallow' | 'cough' | 'other';

/** 嚥下/咳嗽イベント */
export interface MeasurementEvent {
  type: MeasurementEventType;
  /** 開始時刻（秒） */
  startTime: number;
  /** 終了時刻（秒） */
  endTime: number;
  /** 持続時間（ミリ秒） */
  durationMs: number;
}

/** 姿勢データ（POS行から取得） */
export interface PostureDataPoint {
  timeMs: number;
  roll: number;
  pitch: number;
  yaw: number;
}

/** RSST 固有の KPI */
export interface RsstKpi {
  swallowCount: number;
  /** 平均唾液嚥下間隔（秒） */
  meanSwallowInterval: number;
  /** 判定スコア（data.evaluation） */
  evaluation: number;
  /** 基準値未満かどうか */
  isBelowThreshold: boolean;
}

/** MWST 固有の KPI */
export interface MwstKpi {
  /** 最終スコア (1-5) */
  finalScore: number;
  /** 試行回数 */
  attemptCount: number;
  /** 各試行の詳細 */
  attempts: { waterPuddingSwallowPart: any; drySwallowPart: any; interimScore: number }[];
}

/** VS/Monitoring 固有の KPI */
export interface VsKpi {
  swallowCount: number;
  coughCount: number;
  /** 計測時間（秒） */
  measurementDurationSec: number;
}

/** 波形マーカー（WaveSurfer に追加するマーカー情報） */
export interface MarkerInfo {
  time: number;
  color: string;
  label?: string;
  position?: 'top' | 'bottom';
}

/** 波形リージョン（WaveSurfer に追加するリージョン情報） */
export interface RegionInfo {
  id: string;
  start: number;
  end: number;
  color: string;
  showTooltip?: boolean;
}

/** ドーナッツチャート用データ（RSST専用） */
export interface PieChartData {
  values: number[];
  colors: string[];
}

/** CSV解析結果の正規化モデル */
export interface ParsedMeasurement {
  events: MeasurementEvent[];
  markers: MarkerInfo[];
  regions: RegionInfo[];
  postureData: PostureDataPoint[];
  pieChart: PieChartData;
  /** RSST の波形開始オフセット（秒） */
  startOffsetSec: number;
  rsstKpi?: RsstKpi;
  vsKpi?: VsKpi;
  mwstKpi?: MwstKpi;
}

/**
 * processCsvResults の出力を ParsedMeasurement に変換する。
 * 既存の CsvProcessingContext が収集したデータを正規化する。
 */
export function buildParsedMeasurement(
  events: { type: string; startTime: number; endTime: number; duration: number }[],
  pieChartData: number[],
  pieChartColor: string[],
  postureData: PostureDataPoint[],
  startOffsetSec: number,
  recordData?: { data?: { evaluation?: number; numOfSwallowings?: number; numOfCoughs?: number; wavDuration?: number; totalMinutes?: number; recordType?: string; finalScore?: number; attemptsDetail?: any[] } },
): ParsedMeasurement {
  const normalizeEventType = (type: string): MeasurementEventType => {
    if (type === 'swallow') return 'swallow';
    if (type === 'cough') return 'cough';
    return 'other';
  };

  const normalizedEvents: MeasurementEvent[] = events.map(e => ({
    type: normalizeEventType(e.type),
    startTime: e.startTime,
    endTime: e.endTime,
    durationMs: e.duration,
  }));

  const swallowCount = normalizedEvents.filter(e => e.type === 'swallow').length;
  const coughCount = normalizedEvents.filter(e => e.type === 'cough').length;

  let rsstKpi: RsstKpi | undefined;
  let vsKpi: VsKpi | undefined;
  let mwstKpi: MwstKpi | undefined;

  if (recordData?.data) {
    const d = recordData.data;
    const isRsst = d.recordType === 'RSST';
    const isMwst = ['MWST', 'MWST_G', 'MWST_G_DATA', 'FOOD_TEST', 'FOOD_TEST_G', 'FOOD_TEST_G_DATA'].includes(d.recordType ?? '');

    // RSST KPI: evaluation が存在するか、recordType が RSST の場合に生成
    if (d.evaluation !== undefined || isRsst) {
      const swallowEvents = normalizedEvents.filter(e => e.type === 'swallow');
      const meanInterval = swallowEvents.length > 1
        ? swallowEvents.reduce((sum, e, i, arr) => {
            if (i === 0) return 0;
            return sum + (e.startTime - arr[i - 1].endTime);
          }, 0) / (swallowEvents.length - 1)
        : 0;

      const resolvedSwallowCount = d.numOfSwallowings ?? swallowCount;
      rsstKpi = {
        swallowCount: resolvedSwallowCount,
        meanSwallowInterval: meanInterval,
        evaluation: d.evaluation ?? 0,
        isBelowThreshold: resolvedSwallowCount < 3,
      };
    }

    // MWST KPI: finalScore または attemptsDetail が存在する場合に生成
    if (isMwst && (d.finalScore !== undefined || Array.isArray(d.attemptsDetail))) {
      const attempts = Array.isArray(d.attemptsDetail) ? d.attemptsDetail : [];
      mwstKpi = {
        finalScore: d.finalScore ?? 0,
        attemptCount: attempts.length,
        attempts,
      };
    }

    if (d.totalMinutes !== undefined || d.wavDuration !== undefined) {
      vsKpi = {
        swallowCount: d.numOfSwallowings ?? swallowCount,
        coughCount: d.numOfCoughs ?? coughCount,
        measurementDurationSec: d.totalMinutes
          ? d.totalMinutes * 60
          : (d.wavDuration ? d.wavDuration / 1000 : 0),
      };
    }
  }

  return {
    events: normalizedEvents,
    markers: [],
    regions: [],
    postureData,
    pieChart: { values: pieChartData, colors: pieChartColor },
    startOffsetSec,
    rsstKpi,
    vsKpi,
    mwstKpi,
  };
}
